import { SvgIcon, SvgIconProps } from "@mui/material";

interface ClockFilledCircleIconProps extends SvgIconProps {
  /**
   * Colour of the clock hands. They sit ON the filled disc, so this is the
   * contrast colour, not the fill.
   *
   * Required, with no default: a default would have to be a literal hex, and
   * the contrast colour depends on the disc it is drawn on. Callers pass a
   * theme token (`theme.palette.neutral.main`).
   */
  handColor: string;
}

/**
 * Solid clock disc with contrasting hands — the `Waiting on Customer` badge.
 *
 * Two-tone, so it can't reuse `ClockFilledIcon`: that one cuts the hands OUT of
 * the disc, which would show the chip background through them (peach) instead
 * of white.
 *
 * The supplied asset hardcoded the disc as `#C95600` and drew the hands in
 * `currentColor`. That's inverted from how every other icon here works, so the
 * two are swapped: the disc takes `currentColor` (set by the badge via
 * `htmlColor`, i.e. amber.800) and the hands take `handColor`.
 *
 * The circle's stroke follows its FILL, not `handColor`. In the source asset
 * that stroke was `currentColor` — the same orange as the fill, so it was
 * invisible. Painting it with the hand colour instead drew a white ring around
 * the disc. It is kept rather than dropped because a 1.5 stroke centred on
 * r=6.003 is what gives the disc its rendered size.
 */
export const ClockFilledCircleIcon = ({
  handColor,
  ...props
}: ClockFilledCircleIconProps) => (
  <SvgIcon {...props}>
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16">
      <circle
        cx="8.003"
        cy="8.003"
        r="6.003"
        fill="currentColor"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        stroke={handColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m10.307 8.77-2.304-.767V4.002"
      />
    </svg>
  </SvgIcon>
);
